import { combineReducers, Reducer, AnyAction } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import campaignReducer from "./slices/campaign/campaignSlice";
import sessionReducer from "./slices/session/sessionSlice";
import appReducer from "./slices/app/appSlice";
import { usersApi } from "./apis/usersApi";
import { companyApi } from "./apis/companyApi";
import { campaignApi } from "./apis/campaignApi";
import { metricsApi } from "./apis/metricsApi";
import { internalCampaignsMetricsApi } from "./apis/internalCampaignsMetricsApi";
import { internalCampaignsMergeApi } from "./apis/internalCampaignsMergeApi";
import { internalCampaignApi } from "./apis/internalCampaignApi";
import { systemStatsApi } from "./apis/systemStatsApi";
import { creativeMetricsApi } from "./apis/creativeMetricsApi";
import { publisherMetricsApi } from "./apis/publisherMetricsApi";
import { dimensionMetricsApi } from "./apis/dimensionMetricsApi";
import { currencyApi } from "./apis/currencyApi";
import { resetStore } from "./actions";
import { customEntitiesApi } from "./apis/customEntitiesApi";
import { insertionOrderApi } from "./apis/insertionOrderApi";
import { insertionOrderMarginApi } from "./apis/insertionOrderMarginApi";
import { spotifyApi } from "./apis/spotifyApi";
import { dv360Api } from "./apis/dv360Api";
import { hivestackApi } from "./apis/hivestackApi";
import { amazonApi } from "./apis/amazonApi";
import { asyncOperationsApi } from "./apis/asyncOperationsApi";
import { briefingApi } from "./apis/briefingApi";
import { dataModelApi } from "./apis/dataModelApi";
import { wipApi } from "./apis/wipApi";
import { tableViewsApi } from "./apis/tableViewsApi";
import { adminApi } from "./apis/adminApi";
import { ocApi } from "./apis/ocApi";
import { invoiceApi } from "./apis/invoiceApi";
import { controlApi } from "./apis/controlApi";
import { associatedCampaignMarginApi } from "./apis/associatedCampaignMarginApi";
import { clientRegistryApi } from "./apis/clientRegistryApi";
import { commercialProposalApi } from "./apis/commercialProposalApi";
import { commercialDashboardApi } from "./apis/commercialDashboardApi";
import { costAnalysisApi } from "./apis/costAnalysisApi";
import { controlBoardApi } from "./apis/controlBoardApi";
import { mcpApi } from "./apis/mcpApi";
import { serviceBriefingApi } from "./apis/serviceBriefingApi";

// Define the combined reducers
const appReducers = combineReducers({
  app: appReducer,
  session: sessionReducer,
  campaign: campaignReducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [companyApi.reducerPath]: companyApi.reducer,
  [campaignApi.reducerPath]: campaignApi.reducer,
  [metricsApi.reducerPath]: metricsApi.reducer,
  [internalCampaignsMetricsApi.reducerPath]:
    internalCampaignsMetricsApi.reducer,
  [internalCampaignsMergeApi.reducerPath]: internalCampaignsMergeApi.reducer,
  [internalCampaignApi.reducerPath]: internalCampaignApi.reducer,
  [systemStatsApi.reducerPath]: systemStatsApi.reducer,
  [creativeMetricsApi.reducerPath]: creativeMetricsApi.reducer,
  [publisherMetricsApi.reducerPath]: publisherMetricsApi.reducer,
  [dimensionMetricsApi.reducerPath]: dimensionMetricsApi.reducer,
  [currencyApi.reducerPath]: currencyApi.reducer,
  [customEntitiesApi.reducerPath]: customEntitiesApi.reducer,
  [insertionOrderApi.reducerPath]: insertionOrderApi.reducer,
  [insertionOrderMarginApi.reducerPath]: insertionOrderMarginApi.reducer,
  [spotifyApi.reducerPath]: spotifyApi.reducer,
  [dv360Api.reducerPath]: dv360Api.reducer,
  [hivestackApi.reducerPath]: hivestackApi.reducer,
  [amazonApi.reducerPath]: amazonApi.reducer,
  [asyncOperationsApi.reducerPath]: asyncOperationsApi.reducer,
  [briefingApi.reducerPath]: briefingApi.reducer,
  [dataModelApi.reducerPath]: dataModelApi.reducer,
  [wipApi.reducerPath]: wipApi.reducer,
  [tableViewsApi.reducerPath]: tableViewsApi.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
  [ocApi.reducerPath]: ocApi.reducer,
  [invoiceApi.reducerPath]: invoiceApi.reducer,
  [controlApi.reducerPath]: controlApi.reducer,
  [associatedCampaignMarginApi.reducerPath]:
    associatedCampaignMarginApi.reducer,
  [clientRegistryApi.reducerPath]: clientRegistryApi.reducer,
  [commercialProposalApi.reducerPath]: commercialProposalApi.reducer,
  [commercialDashboardApi.reducerPath]: commercialDashboardApi.reducer,
  [costAnalysisApi.reducerPath]: costAnalysisApi.reducer,
  [controlBoardApi.reducerPath]: controlBoardApi.reducer,
  [mcpApi.reducerPath]: mcpApi.reducer,
  [serviceBriefingApi.reducerPath]: serviceBriefingApi.reducer,
});

// Define the root state type based on the appReducers
export type RootState = ReturnType<typeof appReducers>;

// Create a root reducer that can handle the reset action
const rootReducers: Reducer<RootState, AnyAction> = (state, action) => {
  // When the reset action is dispatched, return undefined state
  // which will cause each reducer to return its initial state
  if (action.type === resetStore.type) {
    // Clear storage for redux-persist to prevent hydrating stale user state on next login
    storage.removeItem("persist:root");
    // Clear the state but keep some non-sensitive data if needed
    state = undefined;
  }

  // Pass the state and action to the combined reducers
  return appReducers(state, action);
};

export default rootReducers;
